// =============================================================================
// Login — CCF Front Office sign-in, forgot-password, reset-sent
// File: client/src/pages/Login.jsx
// Version: v2.0  ·  Session: SQ-Chrome S1  ·  2026-08-27
// Change notes:
//   - PRESENTATION ONLY. Every line of auth logic below is byte-identical to
//     v1.x: signInWithPassword, the team-row status/role check, the signOut on
//     denial, resetPasswordForEmail with its redirectTo, and all three modes.
//     Nothing about who can get in changed.
//   - Canvas flipped hunter-deep -> bg-fo-canvas so the front door matches the
//     dashboard. Removes the dark-login -> light-dashboard cut at the exact
//     moment someone enters the app.
//   - The global `.card` class is REPLACED INLINE here rather than edited in
//     index.css, because `.card` is shared with pages outside this pass's
//     scope. Same for nothing else — `.input` and `.btn-primary` are untouched
//     and still come from the global sheet.
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase.js';

// Local card chrome — matches Dashboard's CARD_BASE. Deliberately not written
// into index.css: `.card` is used by pages this session does not own.
const PANEL = 'bg-white rounded-card shadow-card p-6 space-y-4';

export default function Login() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  // 🔴 SIGN-IN LANDS ON THE FRONT OFFICE, NOT THE HUDDLER.
  // It sent everyone to /huddler because when this login was written the
  // Huddler was the only page that existed. It is now one card of nine, and
  // dropping an operator inside a single tool skips the launcher entirely.
  // Jake, SQ-Chrome S1: "once you log in, it redirects you to the huddler
  // (that was the only page built when we created the login). Have it take
  // you to Front Office instead."
  // '/' is Dashboard behind RequireTeamMember (App.jsx:105). Both redirects
  // below must stay in step — this one covers an already-live session, the
  // one in onSignIn covers a fresh sign-in.
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate('/', { replace: true });
    });
  }, [navigate]);

  async function onSignIn(e) {
    e.preventDefault();
    setError(null);
    setAccessDenied(false);
    setLoading(true);

    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) {
      setLoading(false);
      setError(authError.message);
      return;
    }

    const userId = authData.user.id;
    const { data: teamRow, error: teamError } = await supabase
      .from('team')
      .select('status, role')
      .eq('auth_user_id', userId)
      .single();

    const denied =
      teamError ||
      !teamRow ||
      teamRow.status !== 'active' ||
      teamRow.role === 'system';

    if (denied) {
      // S100 Build 2 — a valid login that is not staff may be a PLAYER.
      // member_read_own RLS lets the signed-in kid read exactly their own row,
      // so an empty result here really means "nobody", not "blocked".
      const { data: playerRow } = await supabase
        .from('player_profiles')
        .select('recruit_id')
        .eq('user_id', userId)
        .maybeSingle();
      if (playerRow) {
        setLoading(false);
        navigate('/my-profile', { replace: true });
        return;
      }
      await supabase.auth.signOut();
      setLoading(false);
      setAccessDenied(true);
      return;
    }

    setLoading(false);
    navigate('/', { replace: true });   // Front Office, not the Huddler — see the note above.
  }

  async function onForgot(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    setLoading(false);
    if (resetError) {
      setError(resetError.message);
      return;
    }
    setMode('forgotSent');
  }

  function goBack() {
    setError(null);
    setAccessDenied(false);
    setMode('signin');
  }

  return (
    <div className="min-h-screen bg-fo-canvas flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        <div className="text-center mb-8">
          <h1 className="text-hunter-deep text-3xl font-display font-black tracking-tight">CCF Front Office</h1>
          <p className="text-glyph-green text-xs tracking-[0.3em] uppercase font-bold mt-2">Coach Cole Football</p>
        </div>

        {mode === 'signin' && (
          <form onSubmit={onSignIn} className={PANEL}>
            <h2 className="text-xl font-display font-bold text-hunter-deep">Sign In</h2>

            <div>
              <label className="block text-sm font-bold mb-1">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="input"
                autoComplete="email"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-1">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="input"
                autoComplete="current-password"
              />
            </div>

            {accessDenied && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                Your account isn't authorized for the CCF Front Office. Contact your administrator.
              </div>
            )}
            {error && !accessDenied && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? 'Signing in…' : 'Sign In'}
            </button>

            <button
              type="button"
              onClick={() => { setError(null); setAccessDenied(false); setMode('forgot'); }}
              className="block w-full text-center text-sm text-glyph-green hover:underline mt-1"
            >
              Forgot your password?
            </button>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={onForgot} className={PANEL}>
            <h2 className="text-xl font-display font-bold text-hunter-deep">Reset Password</h2>
            <p className="text-sm text-silver-mute">Enter your email and we'll send you a reset link.</p>

            <div>
              <label className="block text-sm font-bold mb-1">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="input"
                autoComplete="email"
              />
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? 'Sending…' : 'Send Reset Link'}
            </button>

            <button type="button" onClick={goBack} className="block w-full text-center text-sm text-glyph-green hover:underline mt-1">
              Back to Login
            </button>
          </form>
        )}

        {mode === 'forgotSent' && (
          <div className={`${PANEL} text-center`}>
            <h2 className="text-xl font-display font-bold text-hunter-deep">Check Your Email</h2>
            <p className="text-sm text-silver-mute">Check your email for a reset link.</p>
            <button type="button" onClick={goBack} className="btn-primary w-full">
              Back to Login
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
